interface ReportData {
  businessName: string;
  month: string;
  year: number;
  totalPayments: number;
  totalPaymentsUSD: number;
  totalExpensesMVR: number;
  exchangeRevenueMVR: number;
  bankCostMVR: number;
  exchangeProfitMVR: number;
  netProfitMVR: number;
  bankRate: number;
  blackMarketRate: number;
  expensesByCategory: Record<string, number>;
  dailyPayments: { label: string; value: number }[];
  topCustomers: { label: string; value: number }[];
  payments: { date: string; customer: string; amount: number; card: string }[];
  expenses: { date: string; category: string; amount: number; note: string }[];
}

const PAGE_WIDTH = 595;
const PAGE_HEIGHT = 842;
const MARGIN = 40;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;

function createCanvas(width: number, height: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const canvas = document.createElement('canvas');
  const scale = 2;
  canvas.width = width * scale;
  canvas.height = height * scale;
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;
  const ctx = canvas.getContext('2d')!;
  ctx.scale(scale, scale);
  return [canvas, ctx];
}

function drawHeader(ctx: CanvasRenderingContext2D, data: ReportData, y: number): number {
  ctx.fillStyle = '#0d9488';
  ctx.fillRect(0, 0, PAGE_WIDTH, 80);

  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 22px system-ui, -apple-system, sans-serif';
  ctx.fillText(data.businessName, MARGIN, y + 30);

  ctx.font = '13px system-ui, -apple-system, sans-serif';
  ctx.fillText(`Monthly Report  -  ${data.month} ${data.year}`, MARGIN, y + 52);

  ctx.fillStyle = 'rgba(255,255,255,0.6)';
  ctx.font = '10px system-ui, -apple-system, sans-serif';
  ctx.fillText(`Generated ${new Date().toLocaleDateString()}`, PAGE_WIDTH - MARGIN - 120, y + 52);

  return 100;
}

function drawSectionTitle(ctx: CanvasRenderingContext2D, title: string, y: number): number {
  ctx.fillStyle = '#0d9488';
  ctx.fillRect(MARGIN, y, 3, 18);
  ctx.fillStyle = '#1e293b';
  ctx.font = 'bold 14px system-ui, -apple-system, sans-serif';
  ctx.fillText(title, MARGIN + 12, y + 14);
  return y + 30;
}

function drawKPI(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, label: string, value: string, color: string) {
  ctx.fillStyle = '#f8fafc';
  ctx.strokeStyle = '#e2e8f0';
  ctx.lineWidth = 1;
  roundRect(ctx, x, y, w, 56, 6);

  ctx.fillStyle = '#64748b';
  ctx.font = '10px system-ui, -apple-system, sans-serif';
  ctx.fillText(label, x + 12, y + 20);

  ctx.fillStyle = color;
  ctx.font = 'bold 16px system-ui, -apple-system, sans-serif';
  ctx.fillText(value, x + 12, y + 42);
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
}

function drawMiniBarChart(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, data: { label: string; value: number }[], color: string) {
  const maxVal = Math.max(...data.map(d => d.value), 1);
  const barW = Math.min(20, (w - 20) / data.length - 4);
  const chartBottom = y + h - 20;

  data.forEach((d, i) => {
    const bx = x + 10 + i * (barW + 4);
    const barH = (d.value / maxVal) * (h - 40);
    ctx.fillStyle = color;
    ctx.globalAlpha = 0.85;
    ctx.fillRect(bx, chartBottom - barH, barW, barH);
    ctx.globalAlpha = 1;

    ctx.fillStyle = '#64748b';
    ctx.font = '7px system-ui, -apple-system, sans-serif';
    ctx.save();
    ctx.translate(bx + barW / 2, chartBottom + 14);
    ctx.rotate(-0.5);
    ctx.fillText(d.label.slice(0, 5), 0, 0);
    ctx.restore();
  });
}

function drawDonutChart(ctx: CanvasRenderingContext2D, cx: number, cy: number, outerR: number, innerR: number, data: { label: string; value: number; color: string }[]) {
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total === 0) return;

  let angle = -Math.PI / 2;

  data.forEach((d) => {
    const sliceAngle = (d.value / total) * 2 * Math.PI;
    ctx.beginPath();
    ctx.arc(cx, cy, outerR, angle, angle + sliceAngle);
    ctx.arc(cx, cy, innerR, angle + sliceAngle, angle, true);
    ctx.closePath();
    ctx.fillStyle = d.color;
    ctx.fill();
    angle += sliceAngle;
  });
}

function drawTable(ctx: CanvasRenderingContext2D, x: number, y: number, headers: string[], rows: string[][], colWidths: number[]): number {
  const rowH = 20;
  const headerH = 24;

  ctx.fillStyle = '#0d9488';
  ctx.fillRect(x, y, colWidths.reduce((a, b) => a + b, 0), headerH);

  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 9px system-ui, -apple-system, sans-serif';
  let cx = x;
  headers.forEach((h, i) => {
    ctx.fillText(h, cx + 6, y + 15);
    cx += colWidths[i];
  });

  y += headerH;

  rows.forEach((row, ri) => {
    if (ri % 2 === 0) {
      ctx.fillStyle = '#f8fafc';
      ctx.fillRect(x, y, colWidths.reduce((a, b) => a + b, 0), rowH);
    }

    ctx.fillStyle = '#334155';
    ctx.font = '9px system-ui, -apple-system, sans-serif';
    cx = x;
    row.forEach((cell, ci) => {
      const truncated = cell.length > 20 ? cell.slice(0, 19) + '..' : cell;
      ctx.fillText(truncated, cx + 6, y + 14);
      cx += colWidths[ci];
    });

    y += rowH;
  });

  return y + 10;
}

export async function generatePDFReport(data: ReportData): Promise<void> {
  const pages: HTMLCanvasElement[] = [];

  // ---- PAGE 1: Summary ----
  const [page1, ctx1] = createCanvas(PAGE_WIDTH, PAGE_HEIGHT);
  ctx1.fillStyle = '#ffffff';
  ctx1.fillRect(0, 0, PAGE_WIDTH, PAGE_HEIGHT);

  let y = drawHeader(ctx1, data, 20);

  const kpiW = (CONTENT_WIDTH - 24) / 4;
  drawKPI(ctx1, MARGIN, y, kpiW, 'Total Payments', `${data.totalPayments}`, '#1e293b');
  drawKPI(ctx1, MARGIN + kpiW + 8, y, kpiW, 'Revenue USD', `$${data.totalPaymentsUSD.toFixed(2)}`, '#0d9488');
  drawKPI(ctx1, MARGIN + (kpiW + 8) * 2, y, kpiW, 'Exchange Profit', `MVR ${data.exchangeProfitMVR.toFixed(2)}`, '#0d9488');
  drawKPI(ctx1, MARGIN + (kpiW + 8) * 3, y, kpiW, 'Net Profit', `MVR ${data.netProfitMVR.toFixed(2)}`, data.netProfitMVR >= 0 ? '#059669' : '#dc2626');
  y += 76;

  y = drawSectionTitle(ctx1, 'Profit Flow Analysis', y);

  const wfItems = [
    { label: 'Exchange Revenue', value: data.exchangeRevenueMVR, type: 'add' as const },
    { label: 'Bank Cost', value: data.bankCostMVR, type: 'subtract' as const },
    { label: 'Expenses', value: data.totalExpensesMVR, type: 'subtract' as const },
  ];

  const wfMaxVal = Math.max(data.exchangeRevenueMVR, data.bankCostMVR, data.totalExpensesMVR, data.netProfitMVR, 1);
  const wfBarW = 90;
  const wfGap = 30;
  const wfChartH = 120;
  const wfBaseline = y + wfChartH * 0.8;
  let wfCum = 0;

  wfItems.forEach((item, i) => {
    const bx = MARGIN + 20 + i * (wfBarW + wfGap);
    let barStart: number, barEnd: number;

    if (item.type === 'add') {
      barStart = wfCum;
      barEnd = wfCum + item.value;
      wfCum += item.value;
    } else {
      barEnd = wfCum;
      barStart = wfCum - item.value;
      wfCum -= item.value;
    }

    const y1 = wfBaseline - (Math.max(barStart, barEnd) / wfMaxVal) * (wfChartH * 0.7);
    const y2 = wfBaseline - (Math.min(barStart, barEnd) / wfMaxVal) * (wfChartH * 0.7);
    const barH = Math.max(y2 - y1, 2);

    ctx1.fillStyle = item.type === 'add' ? '#10b981' : '#ef4444';
    ctx1.globalAlpha = 0.85;
    roundRect2(ctx1, bx, y1, wfBarW, barH, 4);
    ctx1.globalAlpha = 1;

    ctx1.fillStyle = '#1e293b';
    ctx1.font = 'bold 10px system-ui, -apple-system, sans-serif';
    ctx1.textAlign = 'center';
    ctx1.fillText(`MVR ${item.value.toFixed(0)}`, bx + wfBarW / 2, y1 - 6);

    ctx1.fillStyle = '#64748b';
    ctx1.font = '9px system-ui, -apple-system, sans-serif';
    ctx1.fillText(item.label, bx + wfBarW / 2, wfBaseline + 16);
    ctx1.textAlign = 'left';
  });

  const netBx = MARGIN + 20 + 3 * (wfBarW + wfGap);
  const netBarH = Math.abs(wfCum / wfMaxVal) * (wfChartH * 0.7);
  const netY = wfCum >= 0 ? wfBaseline - netBarH : wfBaseline;
  ctx1.fillStyle = wfCum >= 0 ? '#0d9488' : '#ef4444';
  ctx1.globalAlpha = 0.85;
  roundRect2(ctx1, netBx, netY, wfBarW, Math.max(netBarH, 2), 4);
  ctx1.globalAlpha = 1;
  ctx1.fillStyle = '#1e293b';
  ctx1.font = 'bold 10px system-ui, -apple-system, sans-serif';
  ctx1.textAlign = 'center';
  ctx1.fillText(`MVR ${wfCum.toFixed(0)}`, netBx + wfBarW / 2, netY - 6);
  ctx1.fillStyle = '#64748b';
  ctx1.font = '9px system-ui, -apple-system, sans-serif';
  ctx1.fillText('Net Profit', netBx + wfBarW / 2, wfBaseline + 16);
  ctx1.textAlign = 'left';

  y += wfChartH + 40;

  y = drawSectionTitle(ctx1, 'Exchange Rate Breakdown', y);

  const items = [
    { label: `Revenue (${data.totalPaymentsUSD.toFixed(2)} USD x ${data.blackMarketRate} MVR)`, value: `MVR ${data.exchangeRevenueMVR.toFixed(2)}`, color: '#10b981' },
    { label: `Bank Cost (${data.totalPaymentsUSD.toFixed(2)} USD x ${data.bankRate} MVR)`, value: `-MVR ${data.bankCostMVR.toFixed(2)}`, color: '#ef4444' },
    { label: 'Exchange Profit', value: `MVR ${data.exchangeProfitMVR.toFixed(2)}`, color: '#0d9488', bold: true },
    { label: 'Total Expenses', value: `-MVR ${data.totalExpensesMVR.toFixed(2)}`, color: '#ef4444' },
    { label: 'Net Profit', value: `MVR ${data.netProfitMVR.toFixed(2)}`, color: data.netProfitMVR >= 0 ? '#059669' : '#dc2626', bold: true },
  ];

  items.forEach((item: any) => {
    ctx1.fillStyle = item.bold ? '#f0fdfa' : '#f8fafc';
    ctx1.strokeStyle = item.bold ? '#99f6e4' : '#e2e8f0';
    ctx1.lineWidth = 1;
    roundRect(ctx1, MARGIN, y, CONTENT_WIDTH, 28, 4);

    ctx1.fillStyle = '#475569';
    ctx1.font = `${item.bold ? 'bold ' : ''}10px system-ui, -apple-system, sans-serif`;
    ctx1.fillText(item.label, MARGIN + 12, y + 18);

    ctx1.fillStyle = item.color;
    ctx1.font = `bold 11px system-ui, -apple-system, sans-serif`;
    ctx1.textAlign = 'right';
    ctx1.fillText(item.value, PAGE_WIDTH - MARGIN - 12, y + 18);
    ctx1.textAlign = 'left';

    y += 34;
  });

  y += 10;

  if (Object.keys(data.expensesByCategory).length > 0) {
    y = drawSectionTitle(ctx1, 'Expenses by Category', y);

    const catData = Object.entries(data.expensesByCategory)
      .sort(([, a], [, b]) => b - a)
      .map(([label, value], i) => ({
        label,
        value,
        color: ['#0d9488', '#0ea5e9', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'][i % 6],
      }));

    drawDonutChart(ctx1, MARGIN + 60, y + 60, 50, 30, catData);

    let legendY = y + 10;
    catData.forEach((c) => {
      const pct = data.totalExpensesMVR > 0 ? ((c.value / data.totalExpensesMVR) * 100).toFixed(1) : '0.0';
      ctx1.fillStyle = c.color;
      ctx1.fillRect(MARGIN + 140, legendY, 10, 10);
      ctx1.fillStyle = '#334155';
      ctx1.font = '10px system-ui, -apple-system, sans-serif';
      ctx1.fillText(`${c.label}: MVR ${c.value.toFixed(2)} (${pct}%)`, MARGIN + 156, legendY + 9);
      legendY += 18;
    });

    y = Math.max(y + 130, legendY + 10);
  }

  if (data.dailyPayments.length > 0 && y + 160 < PAGE_HEIGHT - 40) {
    y = drawSectionTitle(ctx1, 'Daily Payment Volume', y);
    drawMiniBarChart(ctx1, MARGIN, y, CONTENT_WIDTH, 120, data.dailyPayments, '#0d9488');
    y += 140;
  }

  ctx1.fillStyle = '#94a3b8';
  ctx1.font = '8px system-ui, -apple-system, sans-serif';
  ctx1.textAlign = 'center';
  ctx1.fillText('Page 1', PAGE_WIDTH / 2, PAGE_HEIGHT - 20);
  ctx1.textAlign = 'left';

  pages.push(page1);

  // ---- PAGE 2: Transactions ----
  const [page2, ctx2] = createCanvas(PAGE_WIDTH, PAGE_HEIGHT);
  ctx2.fillStyle = '#ffffff';
  ctx2.fillRect(0, 0, PAGE_WIDTH, PAGE_HEIGHT);

  ctx2.fillStyle = '#0d9488';
  ctx2.fillRect(0, 0, PAGE_WIDTH, 40);
  ctx2.fillStyle = '#ffffff';
  ctx2.font = 'bold 14px system-ui, -apple-system, sans-serif';
  ctx2.fillText(`${data.businessName}  -  Transaction Details`, MARGIN, 26);

  let y2 = 60;

  if (data.payments.length > 0) {
    y2 = drawSectionTitle(ctx2, `Payments (${data.payments.length})`, y2);
    const paymentRows = data.payments.slice(0, 25).map(p => [
      p.date,
      p.customer,
      `$${p.amount.toFixed(2)}`,
      p.card,
    ]);
    y2 = drawTable(ctx2, MARGIN, y2, ['Date', 'Customer', 'Amount', 'Card'], paymentRows, [110, 180, 80, 145]);
    if (data.payments.length > 25) {
      ctx2.fillStyle = '#94a3b8';
      ctx2.font = 'italic 9px system-ui, -apple-system, sans-serif';
      ctx2.fillText(`...and ${data.payments.length - 25} more payments`, MARGIN + 6, y2);
      y2 += 16;
    }
  }

  y2 += 10;

  if (data.expenses.length > 0 && y2 < PAGE_HEIGHT - 100) {
    y2 = drawSectionTitle(ctx2, `Expenses (${data.expenses.length})`, y2);
    const maxExpRows = Math.min(data.expenses.length, Math.floor((PAGE_HEIGHT - y2 - 60) / 20));
    const expenseRows = data.expenses.slice(0, maxExpRows).map(e => [
      e.date,
      e.category,
      `MVR ${e.amount.toFixed(2)}`,
      e.note,
    ]);
    y2 = drawTable(ctx2, MARGIN, y2, ['Date', 'Category', 'Amount', 'Note'], expenseRows, [100, 100, 100, 215]);
    if (data.expenses.length > maxExpRows) {
      ctx2.fillStyle = '#94a3b8';
      ctx2.font = 'italic 9px system-ui, -apple-system, sans-serif';
      ctx2.fillText(`...and ${data.expenses.length - maxExpRows} more expenses`, MARGIN + 6, y2);
    }
  }

  ctx2.fillStyle = '#94a3b8';
  ctx2.font = '8px system-ui, -apple-system, sans-serif';
  ctx2.textAlign = 'center';
  ctx2.fillText('Page 2', PAGE_WIDTH / 2, PAGE_HEIGHT - 20);
  ctx2.textAlign = 'left';

  pages.push(page2);

  // Combine into downloadable images (pseudo-PDF via print)
  const printWindow = window.open('', '_blank');
  if (!printWindow) return;

  const imagesHtml = pages.map(canvas => `<img src="${canvas.toDataURL('image/png')}" style="width:100%;max-width:595px;page-break-after:always;" />`).join('');

  printWindow.document.write(`<!DOCTYPE html><html><head><title>${data.businessName} - Report ${data.month} ${data.year}</title><style>@media print{body{margin:0;}img{page-break-after:always;}}</style></head><body style="margin:0;padding:0;display:flex;flex-direction:column;align-items:center;background:#f1f5f9;">${imagesHtml}</body></html>`);
  printWindow.document.close();

  setTimeout(() => {
    printWindow.print();
  }, 500);
}

function roundRect2(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
  ctx.fill();
}
